<script setup lang="ts">
import MmLoadingState from '~/components/mm/base/MmLoadingState.vue'
import MmFeedbackState from '~/components/mm/base/MmFeedbackState.vue'

import { isGenerationCreditError } from '~/utils/generation-credit-error.mjs';
import { takePlaybackQueue } from '~/components/mm/music/takeQueue'
import TakeCard from '~/components/mm/music/TakeCard.vue';
import FeedShare from '~/components/mm/music/FeedShare.vue';
import { completeShareGeneration, consumePostCreateShare } from '~/utils/shareAttribution';
import { sendShareGrowthEvent } from '~/utils/shareGrowth';
import CreateTabs from '~/components/mm/create/CreateTabs.vue';
import MmField from '~/components/mm/base/MmField.vue';
import MmTextarea from '~/components/mm/base/MmTextarea.vue';
import MmInput from '~/components/mm/base/MmInput.vue';
import MmButton from '~/components/mm/base/MmButton.vue';
import MmChip from '~/components/mm/base/MmChip.vue';
import MmSwitch from '~/components/mm/base/MmSwitch.vue';
import MmSegmented from '~/components/mm/base/MmSegmented.vue';
import MmDisclosure from '~/components/mm/base/MmDisclosure.vue';
import MmDialog from '~/components/mm/base/MmDialog.vue';
import type { TakeDraft } from '~/components/mm/music/takeDraft';
import MmIcon from '~/components/mm/base/MmIcon.vue';
import MmSkeleton from '~/components/mm/base/MmSkeleton.vue';
import MmToastView from '~/components/mm/base/MmToastView.vue';
import { consumeExtensionFragment } from '~/utils/extension-handoff.mjs';
import { useI18n } from "vue-i18n";
import {
  musicService,
  type MusicTask,
  type GenMusic,
  API_PATHS_MUSIC,
} from "~/services/musicService";
import { useAuthState } from "~/composables/useAuthState";
import { useToast } from "~/composables/useToast";
import { useResizeObserver, useThrottleFn } from "@vueuse/core";
import { usePagination } from "~/composables/usePagination";
import { useMusicCreate } from "~/composables/useMusicCreate";
import { getShareAttribution } from '~/utils/shareAttribution';
import { trackEvent, rememberAnalyticsGeneration, trackAnalyticsGeneration } from "~/utils/analytics";
import { usePaywall } from "~/composables/usePaywall";
import { pricingPath } from "~/utils/checkout-source.mjs";
import { isMusicEligibleForMvDraft } from "~/utils/musicToMvDraft";
import { watchEffect, onActivated, onDeactivated, onBeforeUnmount } from "vue";
import { useSeoMeta, useSession } from "#imports";

const PENDING_GENERATE_KEY = "musicmint_pending_generate";

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const { user, userAccount } = useAuthState();
// 改用useToast
const toast = useToast();

// SEO配置 - 使用新的composable
const { setCreatePageSeo } = useSeo();
setCreatePageSeo();
// 引入认证状态composable
const { isAuthenticated, isAuthReady, fetchAndStoreToken, handleSignIn, refreshUserAccount, totalPoints } =
  useAuthState();
const { openPaywall } = usePaywall();
const { session } = useSession();
// 引入音乐创作状态
const { getSimilarMusicData, clearSimilarMusicData } = useMusicCreate();
const currentlyPlayingId = ref<string | null>(null);
const postCreateShareSong = ref<any>(null);
const postCreateShareParams = (location: string) => ({ is_ios: /iPhone|iPad|iPod/.test(navigator.userAgent), cta_location: location, has_gift: false });
const showPostCreateShare = (task: MusicTask) => {
  const event = completeShareGeneration(task);
  if (event) void sendShareGrowthEvent(event);
  const song = consumePostCreateShare(task.id, user.value?.id || 'browser');
  if (!song) return;
  postCreateShareSong.value = song;
  trackEvent('post_create_share_prompt_view', postCreateShareParams('post_create'));
};
const isCurrentlyPlaying = ref(false);

// 音乐播放器显示状态 - 初始化时检查当前状态
const showMusicPlayer = ref(false);

// 播放状态变化事件处理函数
const handlePlayingStateChanged = ((e: CustomEvent) => {
  const { id, isPlaying } = e.detail;
  currentlyPlayingId.value = id;
  isCurrentlyPlaying.value = isPlaying;
}) as EventListener;

// 监听音乐播放器显示状态变化
const handlePlayerVisibilityChange = ((event: CustomEvent) => {
  showMusicPlayer.value = event.detail.isVisible;
}) as EventListener;

// 初始化时检查音乐播放器状态
const checkInitialPlayerState = () => {
  // 通过自定义事件请求当前状态
  window.dispatchEvent(new CustomEvent("request-music-player-state"));

  // 也可以直接检查DOM中是否存在音乐播放器元素
  const playerElement = document.querySelector(".music-player");
  if (
    playerElement &&
    window.getComputedStyle(playerElement).display !== "none"
  ) {
    showMusicPlayer.value = true;
  }
};

// 用户信息
const userInfo = ref<any>({
  name: "",
});

// 更新用户信息
const updateUserInfo = () => {
  if (user.value) {
    userInfo.value.name = user.value.fullName || t("user.role");
  }
};

// 监听用户信息变化，更新userInfo
watch(
  user,
  () => {
    if (user.value) {
      updateUserInfo();
      resumePendingGenerateAfterLogin();
    }
  },
  { deep: true }
);

// 监听认证状态变化，更新userInfo
watch(isAuthenticated, (newValue: any) => {
  if (newValue) {
    updateUserInfo();
    resumePendingGenerateAfterLogin();
  }
});

// 创作模式
const modes = [
  { key: "normal", label: "create.modes.normal", icon: "i-lucide-music" },
  {
    key: "advanced",
    label: "create.modes.advanced",
    icon: "i-lucide-settings",
  },
];

const currentMode = ref("normal");

// 描述文本
const description = ref("");
interface StyleItem {
  label: string;
  value: string;
}
const styleDescription = ref<StyleItem[]>([]);
const style_str = ref("");
const moodDescription = ref<any>([]); // 新增 mood 描述
const mood_str = ref(""); // 新增 mood 字符串
const maxLength = 300;
const maxStyleLength = 200;
const maxMoodLength = 200; // 新增 mood 最大长度

// 高级模式
const lyrics = ref("");
const maxLyricsLength = 3000;
const songName = ref("");
const maxSongNameLength = 60;
const isGeneratingLyrics = ref(false);

// 声音选择
const voices = [
  { key: '', label: 'create.voice.unspecified' },
  { key: "male", label: "create.voice.male", icon: "i-lucide-user" },
  {
    key: "female",
    label: "create.voice.female",
    icon: "i-lucide-user-profile",
  },
];
// 音乐时长
const durations = [
  { key: 'time_3', label: 'create.duration.time_3' },
  { key: 'time_all', label: 'create.duration.time_all' },
]

const selectedVoice = ref('')
const selectedDuration = ref('time_all') //简单模式音乐时长
// 添加纯音乐选项
const isInstrumental = ref(false);
const settingsOpen = ref(false);
const onSettingsToggle = (event: Event) => {
  settingsOpen.value = (event.target as HTMLDetailsElement).open;
};
const settingsPreview = computed(() => [
  style_str.value.trim() || t('mm.song.style'), mood_str.value.trim() || t('create.mood.label'),
  isInstrumental.value ? t('create.status.instrumental') : t(selectedVoice.value ? `t24b.${selectedVoice.value}Voice` : 't24b.anyVoice'),
  t(selectedDuration.value === 'time_all' ? 't24b.fullSong' : 't24b.standard'),
].join(' · '));


const limitFormText = (value: any, maxLength: number) => {
  if (typeof value === 'undefined' || value === null) return '';
  return String(value).trim().slice(0, maxLength);
};

// 风格数据
const styleList = ref<any[]>([]);
// prompt列表
const promptList = ref<any[]>([]);
// 心情数据
const moodList = ref<any[]>([]);

// 风格列表滚动控制
const normalStyleListRef = ref<HTMLElement | null>(null);
const advancedStyleListRef = ref<HTMLElement | null>(null);
const normalMoodListRef = ref<HTMLElement | null>(null);
const advancedMoodListRef = ref<HTMLElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(true);
// 查询间隔（毫秒）
const queryInterval = ref(3000); // 轮询间隔设置
const canScrollMoodLeft = ref(false);
const canScrollMoodRight = ref(true);

// 本地存储键名
const STORAGE_KEYS = {
  CURRENT_MODE: "music_creator_current_mode",
  DESCRIPTION: "music_creator_description",
  STYLE_DESCRIPTION: "music_creator_style_description",
  STYLE_STR: "music_creator_style_str",
  MOOD_DESCRIPTION: "music_creator_mood_description",
  MOOD_STR: "music_creator_mood_str",
  LYRICS: "music_creator_lyrics",
  SONG_NAME: "music_creator_song_name",
  SELECTED_VOICE: "music_creator_selected_voice",
  SELECTED_DURATION: 'music_creator_selected_duration',
  SELECTED_LYRIC_DURATION: 'music_creator_selected_lyric_duration',
  IS_INSTRUMENTAL: "music_creator_is_instrumental",
};


let draftSavePending = false;
let draftScopeActive = true;
let hasAppliedIncomingMusicData = false;

// 清除本地存储的表单数据
const clearFormDataInLocalStorage = () => {
  draftSavePending = false;
  if (process.client) {
    try {
      Object.values(STORAGE_KEYS).forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error("清除localStorage表单数据失败:", error);
    }
  }
};

// 保存表单数据到localStorage
const saveFormDataToLocalStorage = () => {
  if (!draftScopeActive) return;
  if (process.client) {
    try {
      localStorage.setItem(STORAGE_KEYS.CURRENT_MODE, currentMode.value);
      localStorage.setItem(STORAGE_KEYS.DESCRIPTION, description.value);
      localStorage.setItem(STORAGE_KEYS.STYLE_STR, style_str.value);
      localStorage.setItem(STORAGE_KEYS.MOOD_STR, mood_str.value);
      localStorage.setItem(STORAGE_KEYS.LYRICS, lyrics.value);
      localStorage.setItem(STORAGE_KEYS.SONG_NAME, songName.value);
      localStorage.setItem(STORAGE_KEYS.SELECTED_VOICE, selectedVoice.value);
      localStorage.setItem(
        STORAGE_KEYS.IS_INSTRUMENTAL,
        isInstrumental.value.toString()
      );

      localStorage.setItem(STORAGE_KEYS.SELECTED_DURATION, selectedDuration.value);

      // Empty selections must overwrite the previous saved selections, too.
      localStorage.setItem(STORAGE_KEYS.STYLE_DESCRIPTION, JSON.stringify(styleDescription.value));
      localStorage.setItem(STORAGE_KEYS.MOOD_DESCRIPTION, JSON.stringify(moodDescription.value));
      draftSavePending = false;
    } catch (error) {
      console.error("保存表单数据到localStorage失败:", error);
    }
  }
};

const restoreDraftSelections = (raw: string | null) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((item: any) => {
      if (item && typeof item === 'object' && item.label && item.value) return item;
      const value = typeof item === 'string' ? item : String(item);
      return { label: value, value };
    });
  } catch {
    return [];
  }
};

// 从localStorage恢复表单数据
const loadFormDataFromLocalStorage = () => {
  if (hasAppliedIncomingMusicData) return;
  if (process.client) {
    try {
      // 恢复模式选择
      const savedMode = localStorage.getItem(STORAGE_KEYS.CURRENT_MODE);
      if (savedMode) currentMode.value = savedMode;

      // 恢复描述文本
      const savedDescription = localStorage.getItem(STORAGE_KEYS.DESCRIPTION);
      if (savedDescription) description.value = savedDescription;

      // 恢复风格字符串
      const savedStyleStr = localStorage.getItem(STORAGE_KEYS.STYLE_STR);
      if (savedStyleStr) style_str.value = savedStyleStr;

      const savedStyleDescription = localStorage.getItem(STORAGE_KEYS.STYLE_DESCRIPTION);
      if (savedStyleDescription !== null) {
        styleDescription.value = restoreDraftSelections(savedStyleDescription);
      }

      const savedMood = localStorage.getItem(STORAGE_KEYS.MOOD_STR);
      if (savedMood !== null) mood_str.value = savedMood;
      const savedMoodDescription = localStorage.getItem(STORAGE_KEYS.MOOD_DESCRIPTION);
      if (savedMoodDescription !== null) {
        // Mood options use string values, unlike the style selection objects.
        moodDescription.value = restoreDraftSelections(savedMoodDescription).map(item => String(item.value));
      }

      // 恢复歌词
      const savedLyrics = localStorage.getItem(STORAGE_KEYS.LYRICS);
      if (savedLyrics) lyrics.value = savedLyrics;

      // 恢复歌曲名称
      const savedSongName = localStorage.getItem(STORAGE_KEYS.SONG_NAME);
      if (savedSongName) songName.value = savedSongName;

      // 恢复声音选择
      const savedVoice = localStorage.getItem(STORAGE_KEYS.SELECTED_VOICE);
      if (savedVoice) selectedVoice.value = savedVoice;

      // 恢复音乐时长
      const savedDuration = localStorage.getItem(STORAGE_KEYS.SELECTED_DURATION);
      if (savedDuration) selectedDuration.value = savedDuration;
      
      // 恢复纯音乐设置
      const savedInstrumental = localStorage.getItem(
        STORAGE_KEYS.IS_INSTRUMENTAL
      );
      if (savedInstrumental !== null) {
        isInstrumental.value = savedInstrumental === "true";
      }
    } catch (error) {
      console.error("从localStorage恢复表单数据失败:", error);
    }
  }
};

// 提取应用数据到表单的方法，便于复用
const applyDataToForm = (similarData: any) => {
  hasAppliedIncomingMusicData = true;
  // 切换模式
  if (similarData.mode === "advanced") {
    currentMode.value = "advanced";
  } else {
    currentMode.value = "normal";
  }

  // 填充表单数据
  if (similarData.lyrics) {
    lyrics.value = limitFormText(similarData.lyrics, maxLyricsLength);
  }
  if (similarData.title) {
    songName.value = limitFormText(similarData.title, maxSongNameLength);
  }
  if (similarData.prompt) {
    description.value = limitFormText(similarData.prompt, maxLength);
  }
  if (similarData.tags) {
    // 直接设置标签字符串
    style_str.value = limitFormText(similarData.tags, maxStyleLength);

    // 清空之前的风格描述数组
    styleDescription.value = [];

    // 尝试解析标签为风格数组
    try {
      if (typeof style_str.value === "string") {
        const tagValues = style_str.value
          .split(",")
          .map((tag: string) => tag.trim())
          .filter(Boolean);

        // 为每个标签创建对象并添加到数组
        tagValues.forEach((tag: string) => {
          if (tag) {
            styleDescription.value.push({ label: tag, value: tag });
          }
        });
      }
    } catch (e) {
      styleDescription.value = [];
    }
  }
  if ('mood' in similarData) mood_str.value = limitFormText(similarData.mood, maxMoodLength);
  if ('voice' in similarData) selectedVoice.value = ['', 'female', 'male'].includes(similarData.voice) ? similarData.voice : '';
  if (['time_3', 'time_all'].includes(similarData.duration)) selectedDuration.value = similarData.duration;
  mobileSection.value = 'create';
  // 设置是否为纯音乐
  isInstrumental.value = Boolean(similarData.instrumental);

  // 保存到localStorage
  saveFormDataToLocalStorage();

  // 显示成功提示
  toast.success(t("music.similarMusicApplied"));

  // 确保UI更新
  nextTick(() => {
    checkScrollStatus();
  });
};

// 检查是否有"做同款"数据，并应用
const checkAndApplySimilarMusicData = () => {
  const similarData = getSimilarMusicData();

  if (similarData) {
    applyDataToForm(similarData);
    trackEvent('create_prefill_loaded', {
      source: (similarData as any).source || 'unknown',
      page_path: '/create',
      tags: (similarData as any).tags || '',
      source_cta_location: (similarData as any).sourceCtaLocation || '',
      mode: (similarData as any).mode || currentMode.value,
      prompt_length: description.value.length,
      lyrics_length: lyrics.value.length,
    });

    // 清除"做同款"数据，避免下次进入页面时重复应用
    clearSimilarMusicData();

    return true;
  }
  return false;
};

// 设置节流保存函数，避免频繁写入localStorage
const debouncedSave = useThrottleFn(() => {
  if (draftScopeActive && draftSavePending) saveFormDataToLocalStorage();
}, 1000, true);
const flushDraftSave = () => {
  if (draftSavePending) saveFormDataToLocalStorage();
};
onBeforeUnmount(() => {
  flushDraftSave();
  draftScopeActive = false;
});

// 使用watch监听表单字段变化，自动保存
watch(
  [
    currentMode,
    description,
    style_str,
    styleDescription,
    mood_str,
    moodDescription,
    lyrics,
    songName,
    selectedVoice,
    isInstrumental,
    selectedDuration,
  ],
  () => {
    draftSavePending = true;
    debouncedSave();

    // 设置样式引用，以便正确更新滚动控制
    nextTick(() => {
      checkScrollStatus();
    });
  },
  { deep: true }
);

// 添加自动应用监听函数和reactivity副作用
// 在数据变化时自动应用
watchEffect(() => {
  if (process.client) {
    const similarData = getSimilarMusicData();
    if (similarData) {
      applyDataToForm(similarData);

      // 清除已应用的数据，避免重复应用
      clearSimilarMusicData();
    }
  }
});

// 提取数据加载到setup，支持SSR
// 风格数据加载
if (process.server) {
  // SEO配置
  useSeoMeta({
    title: t("meta.create.title"),
    description: t("meta.create.description"),
    keywords: t("meta.create.keywords"),
    ogTitle: t("meta.create.ogTitle"),
    ogDescription: t("meta.create.ogDescription"),
    ogImage: "/images/ogimage.png",
    twitterCard: "summary_large_image",
  });

  // 服务端只初始化空数组，不进行API调用
  styleList.value = [];
  moodList.value = [];
  promptList.value = [];
} else {
  // 客户端渲染时在mounted之前初始化空数组，在mounted后加载数据
  // 判断styleList是否为空是在mounted中处理的
}

onMounted(() => {
  // 以下是客户端特定的逻辑，页面基本数据（样式列表、心情列表、提示列表）已在setup阶段加载，支持SSR渲染

  // 检查是否有"做同款"数据，如果有则应用
  const extensionDraft = consumeExtensionFragment();
  if (extensionDraft?.kind === 'compose') {
    applyDataToForm({ prompt: extensionDraft.prompt, tags: extensionDraft.style, instrumental: extensionDraft.instrumental, mode: 'normal' });
    trackEvent('extension_handoff_loaded', { action: 'compose', source: 'musicmint_extension' });
  } else {
    checkAndApplySimilarMusicData();
  }

  // 监听播放状态变化事件
  window.addEventListener("playing-state-changed", handlePlayingStateChanged);
  window.addEventListener(
    "music-player-visibility-change",
    handlePlayerVisibilityChange
  );

  // 初始化用户信息
  updateUserInfo();
  resumePendingGenerateAfterLogin();

  // 初始化时检查播放器状态
  checkInitialPlayerState();

  // 监听窗口大小变化，更新滚动按钮状态
  window.addEventListener("resize", checkScrollStatus);
  window.addEventListener("pagehide", flushDraftSave);

  // 如果没有"做同款"数据，则从localStorage恢复表单数据
  loadFormDataFromLocalStorage();

  // 客户端加载数据
  if (styleList.value.length === 0) {
    loadStyleData();
  }

  if (promptList.value.length === 0) {
    loadPromptList();
  }

  // 在数据加载后检查滚动状态
  nextTick(() => {
    checkScrollStatus();
    checkMoodScrollStatus();
  });
});

onUnmounted(() => {
  // 移除播放状态变化事件监听
  window.removeEventListener(
    "playing-state-changed",
    handlePlayingStateChanged
  );
  window.removeEventListener(
    "music-player-visibility-change",
    handlePlayerVisibilityChange
  );
  window.removeEventListener("resize", checkScrollStatus);
  window.removeEventListener("pagehide", flushDraftSave);
});

// 获取当前任务
const getCurrentTasks = computed(() => {
  // 从任务列表中找到所有状态不是3(完成)和4(失败)的音乐任务作为当前任务
  if (musicTaskList.value && musicTaskList.value.length > 0) {
    const currentGeneratingTasks = musicTaskList.value.filter(
      (task) => task.action === "MUSIC" && task.status < 3
    );
    if (currentGeneratingTasks.length > 0) {
      return currentGeneratingTasks;
    }
  }
  // 如果找不到或列表为空，检查是否有当前任务
  return [];
});

// 过滤出不是当前任务的历史任务
const filteredTasks = computed(() => {
  if (!musicTaskList.value) return [];

  // 获取当前正在生成的任务ID列表
  const currentTaskIds = getCurrentTasks.value.map((task) => task.id);

  // 过滤出不在当前任务列表中的其他任务
  return musicTaskList.value.filter(
    (task) => !currentTaskIds.includes(task.id)
  );
});

const lyricsGenerationError = ref('');
const lyricsMonitor = useTaskMonitor<MusicTask>({
  storageKey: () => `musicmint_pending_create_lyrics_${user.value?.id || 'anonymous'}`,
  load: async (id) => {
    const res = await musicService.getMusicTaskResult({ task_id: id });
    if (res.code !== 200) throw new Error(res.msg);
    return res.data;
  },
  complete: (task) => {
    isGeneratingLyrics.value = false;
    if (!task.lyrics_data?.text) { lyricsGenerationError.value = t('create.toast.lyricsGenerationFailed'); return; }
    lyrics.value = task.lyrics_data.text;
    songName.value = task.lyrics_data.title;
    toast.success(t('create.toast.lyricsGenerationSuccess'));
  },
  failed: (task) => { isGeneratingLyrics.value = false; lyricsGenerationError.value = task.fail_reason || t('create.toast.lyricsGenerationFailed'); },
  stopped: () => { isGeneratingLyrics.value = false; },
  connectionMessage: () => t('ux.checkConnection'),
  timeoutMessage: () => t('ux.taskStillRunning'),
});
const { error: lyricsPollingError, activeTaskId: pendingLyricsId } = lyricsMonitor;
const checkLyricsAgain = () => {
  if (!pendingLyricsId.value) return;
  isGeneratingLyrics.value = true;
  lyricsMonitor.start(pendingLyricsId.value);
};
watch(user, (value) => { if (value && lyricsMonitor.restore()) isGeneratingLyrics.value = true; });
onMounted(() => { if (user.value && lyricsMonitor.restore()) isGeneratingLyrics.value = true; });

// 滚动风格列表
const scrollStyleList = (direction: "left" | "right") => {
  // 根据当前模式选择正确的ref
  const styleListRef =
    currentMode.value === "normal" ? normalStyleListRef : advancedStyleListRef;

  if (!styleListRef.value) return;

  const scrollAmount = 300; // 每次滚动的像素
  const currentScroll = styleListRef.value.scrollLeft;

  if (direction === "left") {
    styleListRef.value.scrollLeft = Math.max(0, currentScroll - scrollAmount);
  } else {
    styleListRef.value.scrollLeft = Math.min(
      styleListRef.value.scrollWidth - styleListRef.value.clientWidth,
      currentScroll + scrollAmount
    );
  }

  // 检查滚动状态
  nextTick(() => {
    checkScrollStatus();
  });
};

// 检查滚动状态
const checkScrollStatus = () => {
  // 根据当前模式选择正确的ref
  const styleListRef =
    currentMode.value === "normal" ? normalStyleListRef : advancedStyleListRef;

  if (!styleListRef.value) return;

  const { scrollLeft, scrollWidth, clientWidth } = styleListRef.value;
  canScrollLeft.value = scrollLeft > 0;
  canScrollRight.value = scrollLeft < scrollWidth - clientWidth - 5; // 添加5px的容差
};

// 检查mood滚动状态
const checkMoodScrollStatus = () => {
  // 根据当前模式选择正确的ref
  const moodListRef =
    currentMode.value === "normal" ? normalMoodListRef : advancedMoodListRef;

  if (!moodListRef.value) return;

  const { scrollLeft, scrollWidth, clientWidth } = moodListRef.value;
  canScrollMoodLeft.value = scrollLeft > 0;
  canScrollMoodRight.value = scrollLeft < scrollWidth - clientWidth - 5; // 添加5px的容差
};

watch(locale, () => { if (import.meta.client) { void loadStyleData(); void loadPromptList(); } });

// 加载风格数据
let styleDataRequest = 0;
const loadStyleData = async () => {
  const request = ++styleDataRequest;
  const requestedLocale = locale.value;
  try {
    // 加载热门榜单数据
    const res: any = await musicService.getStyleList();

    // 正确处理API返回数据结构
    if (request !== styleDataRequest || requestedLocale !== locale.value) return;
    if (res && res.data) {
      const styleData = Array.isArray(res.data)
        ? res.data
        : res.data.list || [];
      styleList.value = styleData;
    }

    // 加载心情数据
    const moodRes: any = await musicService.getMoodList();

    // 正确处理API返回数据结构
    if (request !== styleDataRequest || requestedLocale !== locale.value) return;
    if (moodRes && moodRes.data) {
      const moodData = Array.isArray(moodRes.data)
        ? moodRes.data
        : moodRes.data.list || [];
      moodList.value = moodData;
    }

    // 在数据加载后检查滚动状态
    nextTick(() => {
      checkScrollStatus();
      checkMoodScrollStatus(); // 新增检查 mood 滚动状态
    });
  } catch (error) {
    console.error("加载榜单数据失败:", error);
    styleList.value = [];
    moodList.value = []; // 新增 mood 列表清空
    toast.error(t("create.toast.styleListLoadFailed"));
  }
};

// 加载提示列表
const loadPromptList = async () => {
  try {
    const res: any = await musicService.setRandomPrompt();
    // 正确处理API返回数据结构
    if (res && res.data && res.data.list) {
      promptList.value = res.data.list;
    }
  } catch (error) {
    console.error("加载提示列表失败:", error);
    toast.error(t("create.toast.promptListLoadFailed"));
  }
};

// Chips toggle the current text, including any hand-entered values.
const toggleStyle = (item: string) => {
  const values = style_str.value.split(',').map(value => value.trim()).filter(Boolean);
  const index = values.indexOf(item);
  index < 0 ? values.push(item) : values.splice(index, 1);
  style_str.value = values.join(', ').slice(0, maxStyleLength);
  styleDescription.value = style_str.value.split(',').map(value => value.trim()).filter(Boolean).map(value => ({ label: value, value }));
};

// 使用节流函数包装refreshMusicTasks，防止短时间内多次刷新
const throttledRefreshTasks = useThrottleFn(() => {
  if (isAuthenticated.value) {
    refreshMusicTasks();
  }
}, 1000); // 1秒内只执行一次
// 处理任务更新
const isMusicToMvUpsellEnabled = useMusicToMvUpsellFlag();
const mvUpsellTaskId = ref("");
const dismissedMvUpsellTaskIds = ref<Set<string>>(new Set());

const hasPlayableMusicForMv = (task: MusicTask) => {
  return Boolean(task.music_data?.some(isMusicEligibleForMvDraft));
};

const showMvUpsellForTask = (task: MusicTask) => {
  if (!isMusicToMvUpsellEnabled.value) return;
  if (!task.id || task.status !== 3 || !hasPlayableMusicForMv(task)) return;
  if (dismissedMvUpsellTaskIds.value.has(task.id)) return;
  mvUpsellTaskId.value = task.id;
};

const dismissMvUpsell = (taskId: string) => {
  if (!taskId) return;
  dismissedMvUpsellTaskIds.value.add(taskId);
  if (mvUpsellTaskId.value === taskId) {
    mvUpsellTaskId.value = "";
  }
};

const handleTaskUpdated = (updatedTask: MusicTask) => {
  if (updatedTask.status === 3) showPostCreateShare(updatedTask);
  const previous = musicTaskList.value.find(task => task.id === updatedTask.id);
  if (previous?.status === updatedTask.status && updatedTask.status >= 3) { throttledRefreshTasks(); return; }

  // 只在任务成功完成或失败时进行处理
  if (updatedTask.status === 3) {
    // Keep the editable draft after completion (T06).
    saveFormDataToLocalStorage();

    // 刷新 account 数据，获取积分
    refreshUserAccount();

    // 通知用户任务完成
    readyMusic.value = updatedTask.music_data?.[0] || null;
    readyNotice.value = true;
    clearTimeout(readyTimer);
    readyTimer = setTimeout(() => { readyNotice.value = false; }, 8000);
    showMvUpsellForTask(updatedTask);

    // 任务完成时刷新任务列表
    throttledRefreshTasks();
  } else if (updatedTask.status === 4) {
    // 失败状态
    // 通知用户任务失败
    toast.error(updatedTask.fail_reason || t("create.toast.unknownError"));

    // 任务失败时刷新任务列表
    throttledRefreshTasks();
  }
  // 如果任务仍在进行中，不刷新列表
};

// 生成音乐
const isGenerating = ref(false);
const { creationPricing, loading: pricingLoading } = useCreationPricing();
const creationPoints = computed(() => creationPricing.value?.music?.[selectedDuration.value || 'time_all']);
// Balance hints only for signed-in free accounts with a known charge; members keep the plain cost line.
const creditHint = computed<'' | 'last' | 'short'>(() => {
  if (!isAuthenticated.value || !userAccount.value || creationPoints.value === undefined || creationPoints.value <= 0) return '';
  if (totalPoints.value < creationPoints.value) return 'short';
  if (userAccount.value.vip_level <= 0 && totalPoints.value < creationPoints.value * 2) return 'last';
  return '';
});
const openCreditsPaywall = (surface = 'create') => {
  openPaywall({ trigger: 'insufficient_credits', surface, requiredPoints: creationPoints.value });
};
const mobileSection = ref<'create' | 'results'>('create');

const onCreationTabKeydown = (event: KeyboardEvent) => {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  const tablist = event.currentTarget as HTMLElement;
  const tabs = Array.from(tablist.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
  const current = tabs.findIndex(tab => tab === event.target);
  if (current < 0 || !tabs.length) return;
  const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
    : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
  event.preventDefault();
  tabs[next].click();
  tabs[next].focus();
};

const submissionError = ref('');
const validationInput = ref<'description' | 'lyrics' | ''>('');
watch([description, lyrics, currentMode], () => { validationInput.value = ''; });
const focusInvalidCreationInput = (target: 'description' | 'lyrics') => {
  validationInput.value = target;
  nextTick(() => document.querySelector<HTMLTextAreaElement>(target === 'description' ? '#description-input' : '#advanced-panel textarea')?.focus());
};
const isResumingPendingGenerate = ref(false);

const validateGenerateForm = () => {
  validationInput.value = '';
  if (currentMode.value === "normal" && !description.value.trim()) {
    focusInvalidCreationInput('description');

    return false;
  }
  if (currentMode.value === "normal" && description.value.length > maxLength) {
    toast.error(t('mm.create.maxLength', { field: t('create.description.label'), max: maxLength }));
    return false;
  }

  if (currentMode.value === "advanced" && !lyrics.value.trim()) {
    focusInvalidCreationInput('lyrics');

    return false;
  }
  if (currentMode.value === "advanced" && lyrics.value.length > maxLyricsLength) {
    toast.error(t('mm.create.maxLength', { field: t('create.lyrics.label'), max: maxLyricsLength }));
    return false;
  }
  if (style_str.value.length > maxStyleLength) {
    settingsOpen.value = true;
    toast.error(t('mm.create.maxLength', { field: t('mm.song.style'), max: maxStyleLength }));
    return false;
  }
  if (mood_str.value.length > maxMoodLength) {
    settingsOpen.value = true;
    toast.error(t('mm.create.maxLength', { field: t('create.mood.label'), max: maxMoodLength }));
    return false;
  }
  if (songName.value.length > maxSongNameLength) {
    toast.error(t('mm.create.maxLength', { field: t('create.songName.label'), max: maxSongNameLength }));
    return false;
  }
  return true;
};

const savePendingGenerateIntent = () => {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(PENDING_GENERATE_KEY, JSON.stringify({
    mode: currentMode.value,
    source: "create",
    created_at: Date.now(),
  }));
};

const clearPendingGenerateIntent = () => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(PENDING_GENERATE_KEY);
};

const resumePendingGenerateAfterLogin = async () => {
  if (typeof window === "undefined" || isResumingPendingGenerate.value || isGenerating.value) return;
  const rawPendingGenerate = sessionStorage.getItem(PENDING_GENERATE_KEY);
  if (!rawPendingGenerate || !isAuthenticated.value || !user.value) return;

  isResumingPendingGenerate.value = true;
  clearPendingGenerateIntent();
  trackEvent("generate_resume_after_login", {
    page_path: "/create",
    mode: currentMode.value,
  });

  await nextTick();
  await handleGenerate();
  isResumingPendingGenerate.value = false;
};

const handleGenerate = async () => {
  const isCreditFeedbackCurrent = captureCreditFeedback();
  if (isGenerating.value || isGeneratingLyrics.value || pendingLyricsId.value) return;
  submissionError.value = "";
  if (!validateGenerateForm()) return;

  if (!user.value) {
    saveFormDataToLocalStorage();
    savePendingGenerateIntent();
    trackEvent("generate_blocked_login", {
      page_path: "/create",
      mode: currentMode.value,
      description_length: description.value.length,
      lyrics_length: lyrics.value.length,
      tags_length: getTags().length,
    });
    toast.error(t("user.loginToCreateMusic"));
    await handleSignIn();
    return;
  }

  // Known balances are checked before the pending-task limit (T06).
  if (userAccount.value && creationPoints.value !== undefined && totalPoints.value < creationPoints.value) {
    const isVip = userAccount.value.vip_level > 0;
    submissionError.value = t(isVip ? 'create.toast.pointsNotEnoughMember' : 'create.toast.pointsNotEnoughVip');
    trackEvent('generate_blocked_credits', { page_path: '/create', mode: currentMode.value, balance: totalPoints.value, required_points: creationPoints.value, is_vip: isVip });
    openCreditsPaywall();
    return;
  }

  // 检查未完成任务数量限制
  const unfinishedTasks = musicTaskList.value.filter(
    (task) => task.action === "MUSIC" && task.status < 3
  );

  if (unfinishedTasks.length >= 3) {
    toast.error(t("create.toast.tooManyPendingTasks"));
    return;
  }

  // 检查是否选择了声音
  if (!selectedVoice.value) {
    // 默认选择男声
    selectedVoice.value = "male";
  }

  // 检查是否选择了时长
  if (!selectedDuration.value) {
    // 默认选择3min
    selectedDuration.value = 'time_all';
  }  

  // 保存当前表单数据到localStorage，以防生成过程中页面刷新
  saveFormDataToLocalStorage();

  // 设置生成中状态
  isGenerating.value = true;

  try {
    let params: any = {
      name: songName.value || `Music-${new Date().getTime()}`,
    };

    if (currentMode.value === "normal") {
      params.gpt_description_prompt = description.value;
      params.tags = getTags(); // 新增 mood 参数
      params.gender = selectedVoice.value;
      params.task_time = selectedDuration.value;
      params.make_instrumental = isInstrumental.value;
    } else if (currentMode.value === "advanced") {
      params.prompt = lyrics.value;
      params.title = songName.value;
      params.tags = getTags(); // 修改 tags，添加 mood
      params.gender = selectedVoice.value;
      params.task_time = selectedDuration.value;
      params.make_instrumental = isInstrumental.value;
    }

    trackEvent("generate_submit", {
      page_path: "/create",
      mode: currentMode.value,
      description_length: description.value.length,
      lyrics_length: lyrics.value.length,
      tags_length: getTags().length,
      is_logged_in: Boolean(user.value),
    });

    const analyticsOwner = user.value?.id;
    const res: any = await musicService.createMusic(params);

    if (res.code !== 200) {
      // 处理402错误码 - 积分不足
      if (res.code === 402) {
        // 判断用户是否为VIP会员
        const isVip = userAccount.value && userAccount.value.vip_level > 0;
        submissionError.value = t(isVip ? "create.toast.pointsNotEnoughMember" : "create.toast.pointsNotEnoughVip");
        trackEvent("generate_blocked_credits", {
          page_path: "/create",
          mode: currentMode.value,
          balance: totalPoints.value,
          required_points: creationPoints.value || 0,
          is_vip: Boolean(isVip),
        });
        // The form stays as is; the dialog offers plans at the moment of need.
        if (isCreditFeedbackCurrent()) openCreditsPaywall();
      } else {
        // 其他错误码，显示通用错误信息，不直接展示服务端返回的信息
        submissionError.value = t("create.toast.createMusicError");
        toast.error(submissionError.value);
      }

      isGenerating.value = false; // 请求失败，设置isGenerating为false
      return;
    }

    if (analyticsOwner && analyticsOwner === user.value?.id) rememberAnalyticsGeneration(analyticsOwner, res.data?.task_id, getShareAttribution() ? 'share' : 'direct');

    // Open results immediately so the submitted task is visible on mobile.
    mobileSection.value = 'results';
    // 任务提交成功
    toast.success(t("create.toast.taskSubmitted"));

    // 刷新任务列表，获取包含新提交任务的最新列表
    throttledRefreshTasks();

    // 提交成功后立即将isGenerating设置为false，允许用户继续提交
    isGenerating.value = false;
  } catch (error: any) {
    if (isGenerationCreditError(error)) {
      submissionError.value = t("create.toast.pointsNotEnough");
      if (isCreditFeedbackCurrent()) openCreditsPaywall();
    } else {
      submissionError.value = t("create.toast.musicGenerationFailed");
      toast.error(submissionError.value);
    }
    isGenerating.value = false; // 发生错误，设置isGenerating为false
  }
};

// 随机生成描述
const isGeneratingDesc = ref(false);
const generateRandomDesc = async () => {
  if (!promptList.value.length) { toast.info(t('ux.presetsUnavailable')); return; }
  try {
    isGeneratingDesc.value = true;
    // 随机选择一个风格
    const randomIndex = Math.floor(Math.random() * promptList.value.length);
    const randomPrompt = promptList.value[randomIndex];
    description.value = limitFormText(randomPrompt, maxLength);
  } catch (error) {
    console.error("生成随机描述失败:", error);
    toast.error(t("create.toast.randomDescFailed"));
  } finally {
    isGeneratingDesc.value = false;
  }
};

// 随机生成风格
const isGeneratingStyle = ref(false);
const generateRandomStyle = async () => {
  if (!styleList.value.length) { toast.info(t('ux.presetsUnavailable')); return; }
  try {
    isGeneratingStyle.value = true;

    // 随机选择一个风格
    const randomIndex = Math.floor(Math.random() * styleList.value.length);
    const randomStyle = styleList.value[randomIndex];

    // 更新选中状态和输入框
    styleDescription.value.length = 0;
    styleDescription.value.push(randomStyle);
    style_str.value = randomStyle;
    // 模拟接口延迟
    await new Promise((resolve) => setTimeout(resolve, 300));
  } catch (error) {
    console.error("生成风格失败:", error);
    toast.error(t("create.toast.randomStyleFailed"));
  } finally {
    isGeneratingStyle.value = false;
  }
};
// 生成歌词
const handleGenerateLyrics = async () => {
  const isCreditFeedbackCurrent = captureCreditFeedback();
  if (isGeneratingLyrics.value || pendingLyricsId.value) return;
  lyricsGenerationError.value = '';
  if (!user.value) {
    toast.error(t("user.loginToCreateMusic"));
    await handleSignIn();
    return;
  }

  if (!lyrics.value.trim()) {

    return;
  }
  isGeneratingLyrics.value = true;

  try {
    // 这里原来就是鉴权的，暂时不动
    const res: any = await musicService.generateLyrics(
      API_PATHS_MUSIC.LYRICS.GENERATE,
      { prompt: lyrics.value, title: songName.value, tags: getTags() }
    );
    if (res.code !== 200 || !res.data?.task_id) throw Object.assign(new Error(res.msg || 'Missing lyrics task'), { code: res.code });
    const lyricsTaskId = res.data.task_id;
    toast.success(t("create.toast.lyricsTaskSubmitted"));

    // 开始轮询歌词生成结果
    lyricsMonitor.start(lyricsTaskId);
  } catch (error) {
    lyricsGenerationError.value = t("create.toast.lyricsGenerationFailed");
    if (isGenerationCreditError(error)) {
      if (isCreditFeedbackCurrent()) openPaywall({ trigger: 'insufficient_credits', surface: 'create_lyrics' });
    } else toast.error(lyricsGenerationError.value);
    isGeneratingLyrics.value = false; // 出错时重置状态
  }
  // 不在这里设置isGeneratingLyrics为false，而是在任务完成后设置
};

const getTags = () => {
  return `${style_str.value}${mood_str.value ? "," + mood_str.value : ""}`;
};

// 监听模式变化，更新滚动按钮状态
watch(currentMode, () => {
  nextTick(() => {
    checkScrollStatus();
  });
});

// 切换播放状态
const handlePlayMusic = (music: GenMusic) => {
  // 确保播放器显示
  showMusicPlayer.value = true;
};

// 统一的清除文本方法
const clearText = (
  type: "description" | "style" | "lyrics" | "songName" | "mood"
) => {
  switch (type) {
    case "description":
      description.value = "";
      break;
    case "style":
      style_str.value = "";
      styleDescription.value = [];
      break;
    case "mood":
      mood_str.value = "";
      moodDescription.value = [];
      break;
    case "lyrics":
      lyrics.value = "";
      break;
    case "songName":
      songName.value = "";
      break;
    default:
      break;
  }

  // 清除后保存到localStorage
  saveFormDataToLocalStorage();
};

// 判断表单是否有数据
const hasFormData = computed(() => {
  return !!(description.value || lyrics.value || songName.value || style_str.value || mood_str.value);
});

// 一键清空所有表单数据
const clearAllFormData = () => {
  description.value = '';
  lyrics.value = '';
  songName.value = '';
  style_str.value = '';
  styleDescription.value = [];
  mood_str.value = '';
  moodDescription.value = [];
  selectedVoice.value = '';
  saveFormDataToLocalStorage();
};

// Reversible composer edits wrap the existing clear handlers and draft persistence.
const composerFeedback = shallowRef<{ message: string; restore: () => void } | null>(null);
const composerFooter = ref<HTMLElement | null>(null);
const composerFooterHeight = ref(0);
const composerFooterOffset = ref(0);
const composerFeedbackElement = ref<HTMLElement | null>(null);
const composerFeedbackHeight = ref(0);
const positionComposerFeedback = () => {
  if (!composerFeedback.value || !composerFooter.value) return;
  const rect = composerFooter.value.getBoundingClientRect();
  composerFooterOffset.value = rect.height && rect.bottom > 0 && rect.top < window.innerHeight ? window.innerHeight - rect.top : 0;
};
useResizeObserver(composerFooter, () => {
  composerFooterHeight.value = composerFooter.value?.getBoundingClientRect().height || 0;
  positionComposerFeedback();
});
useResizeObserver(composerFeedbackElement, () => {
  composerFeedbackHeight.value = composerFeedbackElement.value?.getBoundingClientRect().height || 0;
  void nextTick(positionComposerFeedback);
});
onMounted(() => {
  window.addEventListener('scroll', positionComposerFeedback, { passive: true });
  window.addEventListener('resize', positionComposerFeedback);
});
onBeforeUnmount(() => {
  window.removeEventListener('scroll', positionComposerFeedback);
  window.removeEventListener('resize', positionComposerFeedback);
});
let composerFeedbackTimer: ReturnType<typeof setTimeout> | undefined;
const dismissComposerFeedback = () => {
  clearTimeout(composerFeedbackTimer);
  composerFeedback.value = null;
};
const focusComposerField = () => nextTick(() => {
  const input = document.getElementById(currentMode.value === 'advanced' ? 'create-lyrics' : 'description-input') as HTMLTextAreaElement | null;
  if (input && !input.disabled) input.focus();
  else document.querySelector<HTMLButtonElement>('.composer-mode-row [aria-selected="true"]')?.focus();
});
const showComposerUndo = (message: string, restore: () => void) => {
  dismissComposerFeedback();
  composerFeedback.value = { message, restore };
  void nextTick(positionComposerFeedback);
  composerFeedbackTimer = setTimeout(dismissComposerFeedback, 10000);
};
const clearComposerField = (type: 'description' | 'lyrics') => {
  const value = type === 'description' ? description.value : lyrics.value;
  clearText(type);
  showComposerUndo(type === 'description' ? 'mm.create.descriptionCleared' : 'mm.create.lyricsCleared', () => {
    if (type === 'description') description.value = value;
    else lyrics.value = value;
  });
  void focusComposerField();
};
const resetComposer = () => {
  const draft = {
    description: description.value, lyrics: lyrics.value, songName: songName.value,
    style: style_str.value, styles: styleDescription.value.map(item => ({ ...item })),
    mood: mood_str.value, moods: moodDescription.value.map((item: any) => item && typeof item === 'object' ? { ...item } : item),
    voice: selectedVoice.value,
  };
  clearAllFormData();
  showComposerUndo('mm.create.formReset', () => {
    description.value = draft.description;
    lyrics.value = draft.lyrics;
    songName.value = draft.songName;
    style_str.value = draft.style;
    styleDescription.value = draft.styles;
    mood_str.value = draft.mood;
    moodDescription.value = draft.moods;
    selectedVoice.value = draft.voice;
  });
  void focusComposerField();
};
const undoComposerEdit = () => {
  composerFeedback.value?.restore();
  dismissComposerFeedback();
  saveFormDataToLocalStorage();
  void focusComposerField();
};
onBeforeUnmount(dismissComposerFeedback);
onDeactivated(dismissComposerFeedback);

// Bind every read to the signed-in identity and wait for its API token.
const musicTasksRequestKey = () => JSON.stringify([
  isAuthReady.value, isAuthenticated.value, user.value?.id, session.value?.id,
]);

// A delayed credit error belongs only to its submitting identity and page visit.
let creditFeedbackActive = true;
let creditFeedbackDisposed = false;
let creditFeedbackEpoch = 0;
const creditFeedbackIdentity = () => musicTasksRequestKey();
watch(creditFeedbackIdentity, () => { ++creditFeedbackEpoch; }, { flush: 'sync' });
onActivated(() => { if (!creditFeedbackDisposed) creditFeedbackActive = true; });
onDeactivated(() => { creditFeedbackActive = false; ++creditFeedbackEpoch; });
onBeforeUnmount(() => { creditFeedbackDisposed = true; creditFeedbackActive = false; ++creditFeedbackEpoch; });
const captureCreditFeedback = () => {
  const identity = creditFeedbackIdentity(), epoch = creditFeedbackEpoch;
  return () => creditFeedbackActive && !creditFeedbackDisposed && epoch === creditFeedbackEpoch && identity === creditFeedbackIdentity();
};

const fetchMyMusicTasks = async (pageNum: number) => {
  if (!isAuthenticated.value || !user.value?.id || !session.value?.id) {
    throw new Error(t('user.not_logged_in'));
  }
  const identity = musicTasksRequestKey();
  const token = await fetchAndStoreToken();
  if (!token || identity !== musicTasksRequestKey()) throw new Error(t('user.sessionExpired'));
  return await musicService.getMyMusicList({ page: pageNum, size: 10 }, { musicmintInlineError: true });
};

// 使用分页hooks获取任务列表
const {
  list: musicTaskList,
  isLoading: isLoadingTasks,
  isLoadingMore: isLoadingMoreTasks,
  hasMore: hasMoreTasks,
  error: tasksError,
  loadMoreTrigger: tasksLoadMoreTrigger,
  loadData: loadMusicTasks,
  reload: refreshMusicTasks,
  retry: retryMusicTasks,
  reset: resetMusicTasks,
} = usePagination<MusicTask>(fetchMyMusicTasks, {
  pageSize: 10,
  autoLoad: false,
  preserveListOnReload: true,
  requestKey: musicTasksRequestKey,
  errorHandler: () => {}, // The result panel owns persistent error and retry feedback.
  dataExtractor: (response) => {
    if (response.code !== 200) throw new Error(t('common.load_failed'));
    if (response.data == null) return [];
    if (Array.isArray(response.data)) return response.data;
    if (typeof response.data === 'object' && Array.isArray(response.data.list)) return response.data.list;
    throw new Error(t('common.load_failed'));
  },
});

// 添加对认证状态的监听，登录后加载任务
watch(musicTaskList, tasks => {
  if (import.meta.client) tasks.filter(task => task.status === 3).forEach(task => {
    trackAnalyticsGeneration(user.value?.id || '', task);
    showPostCreateShare(task);
  });
}, { deep: true });
const { copyrightConfig, taskCertificateStates } = useTaskCertificates(musicTaskList);
const openCopyright = (taskId: string, musicId: string) => {
  const task = musicTaskList.value.find(item => item.id === taskId);
  // Existing single-song orders retain their original View route. New Get
  // entries request one certificate for the complete creation.
  const existing = task && ['issued', 'processing', 'pending'].includes(taskCertificateStates(task)[musicId]);
  return navigateTo({ path: '/copyright', query: { task_id: taskId, ...(existing ? { music_id: musicId } : { scope: 'task' }) } });
};

watch([isAuthReady, isAuthenticated, () => user.value?.id, () => session.value?.id], ([ready, signedIn, owner, sessionId]) => {
  if (!process.client) return;
  resetMusicTasks();
  if (ready && signedIn && owner && sessionId) void loadMusicTasks(1);
}, { immediate: true, flush: 'sync' });

const retryCreateMusicTasks = async () => {
  const panel = document.getElementById('music-create-results');
  const restoreFocus = !!panel?.contains(document.activeElement);
  await retryMusicTasks();
  await nextTick();
  if (restoreFocus && document.activeElement === document.body) panel?.focus();
};

// 滚动 mood 列表
const scrollMoodList = (direction: "left" | "right") => {
  // 根据当前模式选择正确的ref
  const moodListRef =
    currentMode.value === "normal" ? normalMoodListRef : advancedMoodListRef;

  if (!moodListRef.value) return;

  const scrollAmount = 300; // 每次滚动的像素
  const currentScroll = moodListRef.value.scrollLeft;

  if (direction === "left") {
    moodListRef.value.scrollLeft = Math.max(0, currentScroll - scrollAmount);
  } else {
    moodListRef.value.scrollLeft = Math.min(
      moodListRef.value.scrollWidth - moodListRef.value.clientWidth,
      currentScroll + scrollAmount
    );
  }

  // 检查滚动状态
  nextTick(() => {
    checkMoodScrollStatus();
  });
};

const toggleMood = (item: string) => {
  const values = mood_str.value.split(',').map(value => value.trim()).filter(Boolean);
  const index = values.indexOf(item);
  index < 0 ? values.push(item) : values.splice(index, 1);
  mood_str.value = values.join(', ').slice(0, maxMoodLength);
  moodDescription.value = mood_str.value.split(',').map(value => value.trim()).filter(Boolean);
};

// 随机生成 mood
const isGeneratingMood = ref(false);
const generateRandomMood = async () => {
  if (!moodList.value.length) { toast.info(t('ux.presetsUnavailable')); return; }
  try {
    isGeneratingMood.value = true;

    // 随机选择一个心情
    const randomIndex = Math.floor(Math.random() * moodList.value.length);
    const randomMood = moodList.value[randomIndex];

    // 更新选中状态和输入框
    moodDescription.value.length = 0;
    moodDescription.value.push(randomMood);
    mood_str.value = randomMood;
    // 模拟接口延迟
    await new Promise((resolve) => setTimeout(resolve, 300));
  } catch (error) {
    console.error("生成心情失败:", error);
    toast.error(t("create.toast.randomMoodFailed"));
  } finally {
    isGeneratingMood.value = false;
  }
};

// 添加辅助函数
const isStyleSelected = (style: string): boolean => {
  return style_str.value.split(',').map(value => value.trim()).includes(style);
};

useHead(() => ({ htmlAttrs: { lang: locale.value } }));

// V3 presentation and explicitly requested deep-link behavior.
const readyNotice = ref(false);
const readyMusic = ref<GenMusic | null>(null);
let readyTimer: ReturnType<typeof setTimeout> | undefined;
const playReady = () => {
  if (readyMusic.value) window.dispatchEvent(new CustomEvent('play-music', { detail: { ...readyMusic.value, audio_url: readyMusic.value.audio_oss || readyMusic.value.audio_url } }));
  readyNotice.value = false;
};
const playbackQueue = computed(() => takePlaybackQueue(sortedTakes.value));
const sortedTakes = computed(() => [...musicTaskList.value].sort((a, b) => (b.submit_time || b.created || 0) - (a.submit_time || a.created || 0)));
const removeTake = (id: string) => { musicTaskList.value = musicTaskList.value.filter(task => task.id !== id); };
const reuseTake = (draft: any) => {
  description.value = ''; lyrics.value = ''; songName.value = ''; style_str.value = ''; mood_str.value = '';
  styleDescription.value = []; moodDescription.value = [];
  applyDataToForm(draft);
  nextTick(() => document.getElementById(currentMode.value === 'advanced' ? 'create-lyrics' : 'description-input')?.focus());
};
// Preserve all settings for Library reuse; the similar-song adapter intentionally
// normalizes its own, narrower payload and remains unchanged.
const takeToReuse = useState<TakeDraft | null>('mm-take-to-reuse', () => null);
watch([takeToReuse, () => route.path], ([draft, path]) => {
  if (!process.client || path !== '/create' || !draft) return;
  takeToReuse.value = null;
  reuseTake(draft);
}, { immediate: true });
const modeOptions = computed(() => [{ value: 'normal', label: t('mm.create.describe'), panelId: 'normal-panel' }, { value: 'advanced', label: t('mm.create.lyrics'), panelId: 'advanced-panel' }]);
const startFirstSong = async () => { mobileSection.value = 'create'; await nextTick(); document.getElementById(currentMode.value === 'advanced' ? 'create-lyrics' : 'description-input')?.focus(); };
const mobileOptions = computed(() => [{ value: 'create', label: t('nav.create'), panelId: 'music-create-form' }, { value: 'results', label: t('mm.create.yourTakes'), panelId: 'music-create-results' }]);
const voiceOptions = computed(() => ['', 'female', 'male'].map(value => ({ value, label: t(value ? 'create.voice.' + value : 't24b.any'), disabled: isInstrumental.value })));
const lengthOptions = computed(() => durations.map(duration => ({ value: duration.key, label: `${t(duration.key === 'time_all' ? 't24b.fullSong' : 't24b.standard')}${creationPricing.value?.music?.[duration.key] === undefined ? '' : ' · ' + creationPricing.value.music[duration.key]}` })));
const lyricsFullscreen = ref(false);
const aiDialogOpen = ref(false);
const aiTopic = ref('');
const lyricsToolsDisabled = computed(() => isInstrumental.value || isGeneratingLyrics.value || !!pendingLyricsId.value);
const insertSection = (section: string) => {
  const input = document.getElementById(lyricsFullscreen.value ? 'fullscreen-lyrics' : 'create-lyrics') as HTMLTextAreaElement | null;
  if (!input) return;
  const start = input.selectionStart, end = input.selectionEnd;
  const insertion = `${start && lyrics.value[start - 1] !== '\n' ? '\n' : ''}[${section}]\n`;
  if (lyrics.value.length - (end - start) + insertion.length > maxLyricsLength) return;
  lyrics.value = lyrics.value.slice(0, start) + insertion + lyrics.value.slice(end);
  nextTick(() => { input.focus(); input.setSelectionRange(start + insertion.length, start + insertion.length); });
};
const writeWithAI = async () => {
  if (!aiTopic.value.trim()) return;
  lyrics.value = aiTopic.value.trim().slice(0, maxLyricsLength);
  aiDialogOpen.value = false;
  await handleGenerateLyrics();
};
const toolEntries = [
  { route: '/clips', key: 'nav.clips' }, { route: '/mv-generator', key: 'nav.mvGenerator' },
  { route: '/rap-lyrics-generator', key: 'nav.rapLyric' }, { route: '/lyrics-generator', key: 'nav.lyricGenerator' },
  { route: '/album-cover-generator', key: 'nav.albumCover' }, { route: '/rap-name-generator', key: 'nav.rapnameGenerator' },
  { route: '/vocal-separation', key: 'nav.vocalSeparation' },
];
const autoGeneratePending = ref(false);
let incomingRevision = 0;
const applyQuery = async () => {
  const revision = ++incomingRevision;
  const q = route.query;
  const text = (key: string) => typeof q[key] === 'string' ? q[key] as string : '';
  if (text('similar')) {
    const local = musicTaskList.value.flatMap(task => task.music_data || []).find(song => song.id === text('similar'));
    try {
      const response = local ? null : await musicService.getPublicMusicDetail(text('similar'));
      const song = local || (response?.code === 200 ? response.data : null);
      if (revision !== incomingRevision) return;
      if (song) applyDataToForm({ mode: 'advanced', title: song.title, lyrics: song.metadata?.prompt, tags: song.metadata?.tags, prompt: song.metadata?.gpt_description_prompt, instrumental: false });
    } catch { toast.error(t('common.load_failed')); }
  }
  const incoming = ['prompt', 'styles', 'inst', 'mode', 'lyrics', 'title'].some(key => key in q);
  if (incoming) {
    hasAppliedIncomingMusicData = true;
    if ('prompt' in q) description.value = limitFormText(text('prompt'), maxLength);
    if ('styles' in q) { style_str.value = limitFormText(text('styles'), maxStyleLength); styleDescription.value = style_str.value.split(',').map(value => ({ label: value.trim(), value: value.trim() })).filter(value => value.value); }
    if ('inst' in q) isInstrumental.value = text('inst') === '1';
    if ('mode' in q || 'prompt' in q) currentMode.value = text('mode') === 'lyrics' ? 'advanced' : 'normal';
    if ('lyrics' in q) { lyrics.value = limitFormText(text('lyrics'), maxLyricsLength); currentMode.value = 'advanced'; }
    if ('title' in q) songName.value = limitFormText(text('title'), maxSongNameLength);
    saveFormDataToLocalStorage();
  }
  if (q.go === '1') {
    autoGeneratePending.value = true;
    const query = { ...q }; delete query.go;
    await router.replace({ query });
  }
};
watch([autoGeneratePending, isAuthReady, isAuthenticated, userAccount, creationPoints, isLoadingTasks], () => {
  if (!autoGeneratePending.value || !isAuthReady.value) return;
  if (!isAuthenticated.value) { autoGeneratePending.value = false; return; }
  if (!user.value || !userAccount.value || creationPoints.value === undefined || isLoadingTasks.value) return;
  autoGeneratePending.value = false;
  if (totalPoints.value >= creationPoints.value) void handleGenerate();
});
onMounted(() => { void applyQuery(); });
watch(() => [route.query.prompt, route.query.styles, route.query.inst, route.query.mode, route.query.lyrics, route.query.title, route.query.similar], () => { if (process.client) void applyQuery(); });
onBeforeUnmount(() => { incomingRevision++; clearTimeout(readyTimer); });
</script>

<template>
  <div class="mm-root t24b-page create-page wrap">
    <div class="mobile-tabs"><MmSegmented v-model="mobileSection" :options="mobileOptions" :label="t('t24b.createOrTakes')" semantics="tabs" full><template #option="{ option }"><MmIcon v-if="option.value === 'create'" name="plus" :size="16" />{{ option.label }}<span v-if="option.value === 'results' && musicTaskList.length" class="t-mono">· {{ musicTaskList.length }}</span></template></MmSegmented></div>
    <div class="create-layout">
      <form id="music-create-form" class="cp" :data-active="mobileSection === 'create'" :aria-label="t('create.title')" novalidate autocomplete="off" @submit.prevent="handleGenerate">
        <div class="cp-head">
          <i18n-t keypath="mm.create.newSongHeadline" tag="h1" class="t-h1"><template #song><span class="t-serif">{{ t('mm.create.songWord') }}</span></template></i18n-t>
          <MmSegmented v-model="currentMode" :options="modeOptions" :label="t('t24b.creationMode')" semantics="tabs" />
        </div>
        <div v-if="currentMode === 'normal'" id="normal-panel" class="field" role="tabpanel" :aria-label="t('mm.create.describe')">
          <label class="field-label" for="description-input">{{ t('mm.create.about') }}<span class="field-hint">{{ t('t24b.aboutHint') }}</span></label>
          <div class="field-box" :class="{ invalid: validationInput === 'description' }">
            <textarea id="description-input" v-model="description" :maxlength="maxLength" :placeholder="t('t24b.descriptionPlaceholder')" :aria-invalid="validationInput === 'description'" :aria-describedby="validationInput === 'description' ? 'description-error' : undefined" />
            <div class="box-foot"><MmButton variant="ghost" size="sm" :loading="isGeneratingDesc" @click="generateRandomDesc"><MmIcon name="dice" :size="16" />{{ t('mm.create.surprise') }}</MmButton><span class="count">{{ description.length }} / {{ maxLength }}</span></div>
          </div>
          <div v-if="validationInput === 'description'" id="description-error" class="error-text" role="alert"><MmIcon name="alert" :size="14" />{{ t('t24b.descriptionRequired') }}</div>
          <div class="inspo" :aria-label="t('mm.create.styleIdeas')"><MmChip v-for="style in styleList.slice(0, 10)" :key="style" size="sm" :pressed="isStyleSelected(style)" @update:pressed="toggleStyle(style)"><MmIcon name="plus" :size="12" />{{ style }}</MmChip></div>
        </div>
        <template v-else>
          <div class="field"><label class="field-label" for="create-title">{{ t('t24b.title') }}<span class="count">{{ songName.length }} / {{ maxSongNameLength }}</span></label><input id="create-title" v-model="songName" class="input" :disabled="isGeneratingLyrics || !!pendingLyricsId" :maxlength="maxSongNameLength" :placeholder="t('t24b.titlePlaceholder')"></div>
          <div id="advanced-panel" class="field" role="tabpanel" :aria-label="t('mm.create.lyrics')">
            <label class="field-label" for="create-lyrics">{{ t('create.lyrics.label') }}</label>
            <div class="field-box" :class="{ invalid: validationInput === 'lyrics' }">
              <textarea id="create-lyrics" v-model="lyrics" class="lyrics" :maxlength="maxLyricsLength" :placeholder="t('t24b.lyricsPlaceholder')" :disabled="lyricsToolsDisabled" :aria-invalid="validationInput === 'lyrics'" :aria-describedby="validationInput === 'lyrics' ? 'lyrics-error' : undefined" />
              <div class="box-foot"><div class="tool-row"><MmChip v-for="section in ['Verse', 'Chorus', 'Bridge']" :key="section" size="sm" :disabled="lyricsToolsDisabled" @click="insertSection(section)">+ {{ t(`create.lyricsEditor.sections.${section.toLowerCase()}`) }}</MmChip><MmChip size="sm" class="is-on" :disabled="lyricsToolsDisabled" @click="aiTopic = lyrics; aiDialogOpen = true"><MmIcon name="sparkle" :size="14" />{{ t('mm.create.writeAI') }}</MmChip></div><span class="count">{{ lyrics.length }} / {{ maxLyricsLength }}</span></div>
            </div>
            <div v-if="validationInput === 'lyrics'" id="lyrics-error" class="error-text" role="alert"><MmIcon name="alert" :size="14" />{{ t('t24b.lyricsRequired') }}</div>
          </div>
        </template>
        <div class="row2"><MmSwitch v-model="isInstrumental" :label="t('create.status.instrumental')" /></div>
        <MmDisclosure v-model:open="settingsOpen" class="sound-settings" icon="sliders" :label="t('mm.create.soundSettings')" :summary="settingsPreview">
          <div class="field"><label class="field-label" for="create-style">{{ t('mm.song.style') }}<span class="count">{{ style_str.length }} / {{ maxStyleLength }}</span></label><input id="create-style" v-model="style_str" class="input" :maxlength="maxStyleLength" :placeholder="t('t24b.stylePlaceholder')"><div class="cluster"><MmChip v-for="style in styleList.slice(0, 8)" :key="style" size="sm" :pressed="isStyleSelected(style)" @update:pressed="toggleStyle(style)">{{ style }}</MmChip></div></div>
          <div class="field"><label class="field-label" for="create-mood">{{ t('create.mood.label') }}<span class="count">{{ mood_str.length }} / {{ maxMoodLength }}</span></label><input id="create-mood" v-model="mood_str" class="input" :maxlength="maxMoodLength" :placeholder="t('t24b.moodPlaceholder')"><div class="cluster"><MmChip v-for="mood in moodList.slice(0, 8)" :key="mood" size="sm" :pressed="mood_str.split(',').map(value => value.trim()).includes(mood)" @update:pressed="toggleMood(mood)">{{ mood }}</MmChip></div></div>
          <div class="field"><span class="field-label">{{ t('mm.song.voice') }}</span><MmSegmented v-model="selectedVoice" :options="voiceOptions" :label="t('mm.song.voice')" full /></div>
          <div class="field"><span class="field-label">{{ t('mm.song.length') }}<span class="field-hint">{{ t('t24b.costHint') }}</span></span><MmSegmented v-model="selectedDuration" :options="lengthOptions" :label="t('mm.song.length')" full /></div>
        </MmDisclosure>
        <div v-if="lyricsGenerationError || lyricsPollingError" role="alert" class="form-error"><p>{{ lyricsGenerationError || lyricsPollingError }}</p><MmButton v-if="pendingLyricsId && !isGeneratingLyrics" variant="secondary" @click="checkLyricsAgain">{{ t('ux.checkResult') }}</MmButton></div>
        <div v-if="submissionError" role="alert" class="form-error"><p>{{ submissionError }}</p><NuxtLink :to="pricingPath('create_inline_error')">{{ t('nav.pricing') }}</NuxtLink></div>
        <div ref="composerFooter" class="cp-foot create-actions">
          <div class="cost-line"><i18n-t v-if="creationPoints !== undefined" keypath="mm.create.cost" tag="span"><template #points><b>{{ creationPoints }}</b></template></i18n-t><span v-else class="skel cost-skeleton" :aria-label="t('common.loading')" /><i18n-t v-if="isAuthenticated && userAccount" keypath="t24b.creditsLine" tag="span"><template #balance><b>{{ totalPoints }}</b></template><template #more><NuxtLink :to="pricingPath('create_credit_hint')">{{ t('mm.create.getMore') }}</NuxtLink></template></i18n-t></div>
          <MmButton type="submit" size="lg" class="create-submit btn-block" :loading="isGenerating" :disabled="isGeneratingLyrics || !!pendingLyricsId"><MmIcon v-if="!isGenerating" name="sparkle" :size="18" />{{ t(isGenerating ? 'mm.create.starting' : 'nav.create') }}<template v-if="creationPoints !== undefined" #cost><MmIcon name="zap" :size="12" />{{ creationPoints }}</template></MmButton>
        </div>
      </form>
      <section id="music-create-results" class="takes-pane" :data-active="mobileSection === 'results'" :aria-label="t('mm.create.yourTakes')" tabindex="-1">
        <div class="feed-head"><h2 class="t-h2">{{ t('mm.create.yourTakes') }}</h2><NuxtLink class="see-all" to="/library">{{ t('t24b.openLibrary') }}<MmIcon name="right" :size="16" /></NuxtLink></div>
        <MmFeedbackState v-if="tasksError" kind="error" :title="t('common.load_failed')"><MmButton variant="secondary" :loading="isLoadingTasks || isLoadingMoreTasks" @click="retryCreateMusicTasks">{{ t('common.retry') }}</MmButton></MmFeedbackState>
        <MmLoadingState v-if="(!isAuthReady || isLoadingTasks) && !musicTaskList.length" layout="cards" />
        <MmFeedbackState v-else-if="!musicTaskList.length && !tasksError" :title="t('t24b.noTakes')" :description="t('t24b.noTakesHint')"><MmButton class="empty-create-action" @click="startFirstSong">{{ t('copyright.createFirst') }}</MmButton></MmFeedbackState>
        <div class="feed" aria-live="polite"><TakeCard v-for="task in sortedTakes" :key="task.id" :task="task" :playback-queue="playbackQueue" :user-name="userInfo.name" :copyright-entry="isAuthenticated" :copyright-enabled="!!copyrightConfig?.eligible" :copyright-states="taskCertificateStates(task)" :mv-upsell-task-id="mvUpsellTaskId" @play="handlePlayMusic" @task-updated="handleTaskUpdated" @copyright="openCopyright" @mv-upsell-dismissed="dismissMvUpsell" @reuse="reuseTake" @removed="removeTake" /></div>
        <div v-if="musicTaskList.length" ref="tasksLoadMoreTrigger" class="more-zone"><p v-if="isLoadingMoreTasks" role="status">{{ t('common.loading') }}</p></div>
      </section>
    </div>
    <MmDialog v-model="aiDialogOpen" :title="t('t24b.aiTitle')" :description="t('t24b.aiHint')"><div class="field"><label class="field-label" for="ai-topic">{{ t('t24b.topic') }}</label><input id="ai-topic" v-model="aiTopic" class="input" :maxlength="maxLyricsLength" :placeholder="t('t24b.topicPlaceholder')"></div><template #actions><MmButton variant="secondary" @click="aiDialogOpen = false">{{ t('common.cancel') }}</MmButton><MmButton :disabled="!aiTopic.trim()" @click="writeWithAI"><MmIcon name="sparkle" :size="16" />{{ t('t24b.writeIt') }}</MmButton></template></MmDialog>
    <Teleport to="body"><div v-if="readyNotice" class="mm-root"><div class="toasts"><MmToastView :message="t('mm.take.ready')" :action="t('mm.take.playA')" @action="playReady" /></div></div></Teleport>
    <div v-if="composerFeedback" ref="composerFeedbackElement" class="toasts composer-feedback" :style="{ '--composer-footer-height': `${composerFooterHeight}px`, '--composer-footer-offset': `${composerFooterOffset}px` }"><MmToastView :message="t(composerFeedback.message)" :action="t('mm.take.undo')" @action="undoComposerEdit" /></div>
  <FeedShare :song="postCreateShareSong" :heading="t('share.conversion.postCreateTitle')" @close="postCreateShareSong = null" @shared="trackEvent('post_create_share_prompt_click', postCreateShareParams('post_create'))" />
  </div>
</template>


<style scoped>
/* These pages are themselves the wrap, rather than a child of mm-root. */
.mm-root.wrap { width: 100%; max-width: var(--maxw); margin-inline: auto; padding-inline: var(--gutter); }

  .create-layout { display: grid; grid-template-columns: minmax(0, 1fr); gap: 20px; padding-top: 12px; }
  @media (min-width: 1024px) { .create-layout { grid-template-columns: 440px minmax(0, 1fr); gap: 40px; align-items: start; padding-top: 24px; } }
  @media (min-width: 1280px) { .create-layout { grid-template-columns: 468px minmax(0, 1fr); } }

  .mobile-tabs { position: sticky; top: 56px; z-index: 5; padding: 8px 0; background: color-mix(in srgb, var(--bg) 85%, transparent); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); }
  @media (min-width: 1024px) { .mobile-tabs { display: none; } }
  @media (max-width: 1023px) {
    #music-create-form[data-active="false"], #music-create-results[data-active="false"] { display: none; }
    :global(.app-shell.is-create:has(#music-create-form[data-active="true"]) .dock) { transform: translate(-50%, calc(100% + var(--tab-h) + 40px)); visibility: hidden; }
    :global(.app-shell.is-create:has(#music-create-form[data-active="true"])) { padding-bottom: calc(var(--tab-h) + var(--safe-b)); }
    :global(.shell-content:has(> .create-page)) { padding-bottom: 0; }
    .cp-head h1 { font-size: 26px; }
  }

  /* Composer panel */
  .cp { display: grid; gap: 20px; }
  @media (min-width: 1024px) {
    .cp { position: sticky; top: calc(var(--nav-h) + 12px); max-height: calc(100dvh - var(--nav-h) - var(--dock-h) - 44px); overflow: auto; padding: 22px 22px 0; border-radius: var(--r-xl); background: var(--surface); border: 1px solid var(--line); scrollbar-width: thin; }
  }
  .cp-head { display: flex; align-items: end; justify-content: space-between; gap: 12px; }
  .cp-head h1 { font-size: 30px; }
  .cp-head h1 .t-serif { color: var(--live); font-size: 1.12em; }
  .field-box { position: relative; border-radius: var(--r-lg); background: var(--bg-raised); border: 1px solid var(--line-strong); transition: border-color var(--d-quick), box-shadow var(--d-quick); }
  @media (max-width: 1023px) { .field-box { background: var(--surface); } }
  .field-box:focus-within { border-color: var(--accent); box-shadow: 0 0 0 4px var(--accent-soft); }
  .field-box.invalid { border-color: var(--danger); box-shadow: 0 0 0 4px var(--danger-soft); }
  .field-box textarea { display: block; width: 100%; border: 0; outline: none; background: transparent; resize: none; color: var(--text); padding: 14px 14px 4px; font: 500 17px/1.5 var(--font-ui); min-height: 120px; }
  .field-box textarea.lyrics { font: 400 15px/1.75 var(--font-mono); min-height: 260px; resize: vertical; }
  .field-box .box-foot { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 6px 8px 8px 12px; }
  .tool-row { display: flex; gap: 6px; flex-wrap: wrap; }
  .inspo { display: flex; gap: 8px; overflow-x: auto; scrollbar-width: none; margin-top: 10px; padding-bottom: 2px; }
  .inspo::-webkit-scrollbar { display: none; }
  .similar { display: flex; align-items: center; gap: 12px; padding: 10px 10px 10px 12px; border-radius: var(--r-md); background: var(--live-soft); border: 1px solid color-mix(in srgb, var(--live) 40%, transparent); }
  .similar .cover { width: 40px; border-radius: var(--s-2); flex: none; }
  .row2 { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
  .cp-foot { position: sticky; bottom: 0; z-index: 2; display: grid; gap: 10px; padding: 14px 0 18px; background: linear-gradient(180deg, transparent, var(--surface) 26%); }
  @media (max-width: 1023px) { .cp-foot { bottom: calc(var(--tab-h) + var(--safe-b)); padding: 14px 0 12px; background: linear-gradient(180deg, transparent, var(--bg) 30%); } }
  .cost-line { display: flex; justify-content: space-between; align-items: center; font: 500 12.5px/1.2 var(--font-mono); color: var(--text-3); }
  .cost-line b { color: var(--text); font-weight: 600; }
  /* Takes feed */
  .feed-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
  .feed { display: grid; gap: 16px; }

:global(.shell-content:has(> .create-page)) { padding: 0; }
.create-page { color: var(--text); font-family: var(--font-ui); }
.cp, .takes-pane { min-width: 0; }
.field-hint { font-weight: 400; }
.field-box textarea:focus-visible { outline: none; }
.inspo { padding-block: 8px; margin-block: 2px -6px; }
.inspo :deep(.chip) { flex: none; }
.cp-foot .cost-line a { text-decoration: underline; text-underline-offset: 3px; }
.empty .t-h2 { font-size: 20px; margin-bottom: 6px; }
.form-error { color: var(--danger); display: grid; gap: 12px; }
.cost-skeleton { width: 120px; height: 15px; }
.create-page .cp :deep(.sound-settings > summary strong) { font-weight: 700; }
@media (min-width: 1024px) { .cp { scrollbar-width: none; } .cp::-webkit-scrollbar { display: none; } }

</style>

<style scoped>
@media(min-width:1024px) { .empty-create-action { display: none; } }
</style>
